import React from 'react';

import { StarRating } from '../../atoms/StarRating/StarRating';
import { getRelativeDateLabel } from './getRelativeDateLabel';
import { ReviewCardPhotos } from './ReviewCardPhotos';
import { ReviewCardReply } from './ReviewCardReply';
import { ReviewCardReviewer } from './ReviewCardReviewer';
import type { ReviewCardProps } from './types';
import * as styles from './ReviewCard.styles';

/**
 * One review with nothing held back: every photo, the whole review, the whole reply.
 *
 * Where `ReviewPreviewCard` is a preview that leads somewhere, this is the somewhere. It
 * is what a customer sees after "Read more", a photo or the reply row, so it has no
 * controls that lead anywhere and nothing to expand: everything is already open.
 *
 * That is why the preview is its own component rather than a mode of this one. What the
 * two have in common lives here and is shared as code, not copied: the rating, the
 * reviewer footer, the partner's logo, the relative date labels and the card's chrome.
 * The rest is a different composition: a photo carousel instead of a thumbnail row,
 * unclamped text instead of a clamp, and the reply as text instead of a call to action.
 *
 * The card owns no drawer of its own. A consumer puts it in whatever panel it opens.
 */
export const ReviewCard = ({
  date,
  initialPhotoIndex = 0,
  onPhotoNavigate,
  review,
}: ReviewCardProps) => {
  /* One reading of the clock for both dates on the card. Two calls to
     `getRelativeDateLabel` without it could in principle straddle a boundary and put the
     review and the reply on different rungs. */
  const readAt = date ?? new Date();
  const dateLabel = getRelativeDateLabel(review.date, readAt);
  const replyDateLabel = review.reply?.date && getRelativeDateLabel(review.reply.date, readAt);
  const photos = review.photos ?? [];
  /* A consumer passing the index of a photo that is no longer there, because the review
     was refetched between the tap and the open, gets the nearest one rather than a blank
     track. */
  const safePhotoIndex = Math.min(Math.max(0, initialPhotoIndex), photos.length - 1);

  return (
    <article
      /* Same reasoning as `ReviewPreviewCard`: the card has no title of its own, so a screen
         reader arriving at it has nothing to say what it is. The rating is left out
         because `StarRating` announces itself. */
      /* Without a readable date there is nothing to qualify the region with, so the
         label says only what the card is. */
      aria-label={dateLabel ? `Customer review, ${dateLabel}` : 'Customer review'}
      css={styles.card}
      data-testid="review-card"
    >
      {photos.length > 0 && (
        <ReviewCardPhotos
          initialPhotoIndex={safePhotoIndex}
          onNavigate={onPhotoNavigate}
          photos={photos}
        />
      )}

      <div css={styles.content}>
        <div css={styles.ratingRow}>
          <StarRating rating={review.rating} />
          {dateLabel && (
            <time css={styles.date} dateTime={review.date}>
              {dateLabel}
            </time>
          )}
        </div>

        {review.text && <p css={styles.text}>{review.text}</p>}

        <ReviewCardReviewer reviewer={review.reviewer} />
      </div>

      {review.reply && <ReviewCardReply dateLabel={replyDateLabel} reply={review.reply} />}
    </article>
  );
};

ReviewCard.displayName = 'ReviewCard';
